import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { useTorchDirectoryFull, useTorchliteFeed } from '@/hooks/useTorchQueries';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import type { ParsedChain, AcceptLink } from '@/lib/torchTypes';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

import { Flame, ArrowRight, Plus, Zap, Clock, MessageSquare, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

/** Strip app URL and hashtag from note content */
function cleanContent(raw: string): string {
  return raw
    .replace(/https:\/\/torchlite\.shakespeare\.wtf\/torch\/\S+/g, '')
    .replace(/#torchlite/gi, '')
    .trim();
}

const NPUB_MENTION_RE = /nostr:npub1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]+/gi;

/** Render an npub mention as @username linking to the profile */
function MentionedName({ npub }: { npub: string }) {
  const pubkey = useMemo(() => {
    try {
      return nip19.decode(npub).data as string;
    } catch {
      return null;
    }
  }, [npub]);

  const { data: author } = useAuthor(pubkey ?? undefined);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey ?? npub);

  return (
    <Link to={`/${npub}`} className="text-orange-400 hover:underline font-medium">
      @{displayName}
    </Link>
  );
}

/** Split note content and replace nostr:npub mentions with linked @usernames */
function ContentWithMentions({ content }: { content: string }) {
  const parts = content.split(NPUB_MENTION_RE);
  const matches = content.match(NPUB_MENTION_RE) ?? [];

  return (
    <>
      {parts.map((part, i) => (
        <span key={`part-${i}`}>
          {part}
          {matches[i] && <MentionedName key={`mention-${i}`} npub={matches[i].replace('nostr:', '')} />}
        </span>
      ))}
    </>
  );
}

/** A single community note card for the scrolling grid */
function CommunityNoteCard({ event }: { event: NostrEvent }) {
  const { data: author } = useAuthor(event.pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const npub = useMemo(() => { try { return nip19.npubEncode(event.pubkey); } catch { return ''; } }, [event.pubkey]);

  const timeAgo = useMemo(() => {
    const diff = Math.floor(Date.now() / 1000) - event.created_at;
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }, [event.created_at]);

  const content = cleanContent(event.content);
  if (!content) return null;

  return (
    <Link to={`/${npub}`} className="block w-72 shrink-0 group">
      <Card className="h-full border-border/50 hover:border-orange-500/40 transition-colors duration-200">
        <CardContent className="p-4 flex flex-col gap-3 h-full">
          <div className="flex items-center gap-2.5">
            <Avatar className="w-8 h-8 shrink-0 ring-1 ring-border group-hover:ring-orange-400 transition-all">
              <AvatarImage src={picture} alt={displayName} />
              <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm leading-tight truncate group-hover:text-orange-400 transition-colors">
                {displayName}
              </p>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </div>
          <p className="text-sm text-foreground/85 leading-relaxed line-clamp-4 flex-1">
            <ContentWithMentions content={content} />
          </p>
        </CardContent>
      </Card>
    </Link>
  );
}

// Minimum number of card-copies in the track so the loop never shows a gap.
// Each card is 288px + 12px gap = 300px. Viewport max is ~1920px, so we need
// at least ceil(1920/300) = 7 cards visible at once. We keep enough copies
// that the total track width is always >> 2× the viewport.
const MIN_COPIES = 8;

/** Single-row infinite marquee community section */
function CommunitySection({ notes, loading }: { notes: NostrEvent[] | undefined; loading: boolean }) {
  const valid = useMemo(() =>
    (notes ?? []).filter(e => cleanContent(e.content).length > 0),
    [notes]
  );

  // Repeat the array enough times that the track is always wide enough to loop
  // seamlessly no matter how few posts there are.
  const looped = useMemo(() => {
    if (valid.length === 0) return [];
    const copies = Math.ceil(MIN_COPIES / valid.length);
    // We need 2× the repeated set so translateX(-50%) always lands on the seam
    return Array.from({ length: copies * 2 }, () => valid).flat();
  }, [valid]);

  // Duration: ~12 s per unique card, min 30 s
  const duration = Math.max(30, valid.length * 12);

  const skeletonCard = (key: number) => (
    <div key={key} className="w-72 shrink-0">
      <Card className="border-border/50">
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <Skeleton className="w-8 h-8 rounded-full shrink-0" />
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="border-t border-border/40 bg-gradient-to-b from-orange-950/10 to-background py-10">
      {/* Heading */}
      <div className="max-w-6xl mx-auto px-4 mb-6 flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500/15 border border-orange-500/20 shrink-0">
          <MessageSquare className="w-4 h-4 text-orange-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold leading-tight">Community</h2>
          <p className="text-sm text-muted-foreground">#torchlite posts from torch holders</p>
        </div>
      </div>

      {/* Empty state */}
      {!loading && valid.length === 0 && (
        <div className="max-w-6xl mx-auto px-4">
          <Card className="border-dashed">
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                No community posts yet. When torch holders pass and post with{' '}
                <span className="font-mono text-orange-500">#torchlite</span>, they'll appear here.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Marquee row — overflow hidden on wrapper, NO overflow-x on the track */}
      {(loading || valid.length > 0) && (
        <div className="relative overflow-hidden">
          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10 bg-gradient-to-l from-background to-transparent" />

          {loading ? (
            /* Static skeleton row while data loads */
            <div className="flex gap-3 px-4">
              {[1,2,3,4,5].map(skeletonCard)}
            </div>
          ) : (
            <div
              className="flex gap-3 w-max motion-safe:animate-[marquee_var(--dur)_linear_infinite] motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:px-4"
              style={{ '--dur': `${duration}s` } as React.CSSProperties}
            >
              {looped.map((e, i) => (
                <CommunityNoteCard key={`${e.id}-${i}`} event={e} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function TorchCardSkeleton() {
  return (
    <Card className="h-full">
      <CardContent className="p-5 flex flex-col h-full">
        {/* Header — matches title + badge row */}
        <div className="flex items-start justify-between mb-3 gap-2">
          <Skeleton className="h-5 w-32 flex-1" />
          <Skeleton className="h-5 w-14 shrink-0 rounded-full" />
        </div>
        {/* Torch SVG placeholder */}
        <div className="flex justify-center mb-3">
          <Skeleton className="w-28 h-36 rounded-lg" />
        </div>
        {/* CurrentHolderMini row */}
        <div className="flex items-center gap-2 mt-auto">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-3 w-24" />
        </div>
        {/* Footer row */}
        <div className="flex items-center justify-between mt-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      </CardContent>
    </Card>
  );
}

/** Live countdown that ticks every second */
function LiveCountdown({ targetTs }: { targetTs: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, targetTs - now);
  if (remaining === 0) return <span className="text-yellow-600 font-semibold text-xs">Dormant</span>;

  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  if (d > 0) return <span className="text-orange-600 font-mono font-semibold text-xs">{d}d {h}h {m}m</span>;
  if (h > 0) return <span className="text-orange-500 font-mono font-semibold text-xs">{h}h {m}m {s}s</span>;
  return <span className="text-red-500 font-mono font-bold text-xs animate-pulse">{m}m {s}s</span>;
}

/** Compact holder row for the directory card */
function CurrentHolderMini({ pubkey }: { pubkey: string }) {
  const { data: author } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-2 mt-2">
      <Avatar className="w-5 h-5 ring-1 ring-orange-400 ring-offset-1">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-[9px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <span className="text-xs text-muted-foreground truncate">
        <span className="text-muted-foreground/60">Held by </span>
        <span className="font-medium text-foreground">{displayName}</span>
      </span>
    </div>
  );
}

/** Directory card built from a fully-resolved ParsedChain */
function TorchDirectoryCard({ parsedChain }: { parsedChain: ParsedChain }) {
  const { genesis } = parsedChain;
  const state = parsedChain.state;
  const expiresAt = parsedChain.expiresAt;
  const totalHolders = parsedChain.chainLength + 1;
  const currentHolderPubkey = parsedChain.currentHolder ?? genesis.pubkey;

  const chain: AcceptLink[] = useMemo(() =>
    parsedChain.canonicalChain.map(link => ({
      holderPubkey: link.pass.recipientPubkey,
      zapAmountSats: link.pass.zapAmountSats,
      positionIndex: link.position,
    })),
    [parsedChain]
  );

  const stateBadge = {
    lit:          { label: '🔥 Lit',  className: 'bg-orange-500' },
    extinguished: { label: '🪦 Gone', className: 'bg-gray-500' },
  }[state];

  return (
    <Link to={`/torch/${genesis.slug}`}>
      <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer h-full group">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3 gap-2">
            <h3 className="text-base font-semibold group-hover:text-orange-600 transition-colors leading-tight line-clamp-2">
              {genesis.title}
            </h3>
            <Badge className={`${stateBadge.className} text-white text-xs shrink-0`}>
              {stateBadge.label}
            </Badge>
          </div>

          <div className="flex justify-center mb-3">
            <TorchSvg
              genesisId={genesis.id}
              chain={chain}
              state={state}
              className="w-28 h-36 group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          <CurrentHolderMini pubkey={currentHolderPubkey} />

          <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
            <span>{totalHolders} holder{totalHolders !== 1 ? 's' : ''}</span>
            {state === 'lit' && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <LiveCountdown targetTs={expiresAt} />
              </div>
            )}
            {state === 'extinguished' && (
              <span className="text-gray-400">Sealed</span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/** Like TorchDirectoryCard but with a Pass button — used in the "Your Turn" panel */
function MyTorchCard({ parsedChain }: { parsedChain: ParsedChain }) {
  const { genesis } = parsedChain;
  const totalHolders = parsedChain.chainLength + 1;
  const currentHolderPubkey = parsedChain.currentHolder ?? genesis.pubkey;

  const chain: AcceptLink[] = useMemo(() =>
    parsedChain.canonicalChain.map(link => ({
      holderPubkey: link.pass.recipientPubkey,
      zapAmountSats: link.pass.zapAmountSats,
      positionIndex: link.position,
    })),
    [parsedChain]
  );

  return (
    <Card className="border-orange-500/30 hover:border-orange-500/60 transition-all duration-300 h-full group">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start justify-between mb-3 gap-2">
          <Link to={`/torch/${genesis.slug}`} className="font-semibold text-base group-hover:text-orange-400 transition-colors leading-tight line-clamp-2">
            {genesis.title}
          </Link>
        </div>

        <Link to={`/torch/${genesis.slug}`} className="flex justify-center mb-3">
          <TorchSvg
            genesisId={genesis.id}
            chain={chain}
            state="lit"
            className="w-28 h-36 group-hover:scale-105 transition-transform duration-500"
          />
        </Link>

        <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto mb-3">
          <span>{totalHolders} holder{totalHolders !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <LiveCountdown targetTs={parsedChain.expiresAt} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Link to={`/pass/${genesis.slug}`} onClick={e => e.stopPropagation()}>
            <Button className="w-full bg-orange-500 hover:bg-orange-600 gap-2" size="sm">
              <Zap className="w-3.5 h-3.5" />
              Pass the Torch
            </Button>
          </Link>
          <Link
            to={`/torch/${genesis.slug}`}
            onClick={e => e.stopPropagation()}
            className="text-center text-xs text-muted-foreground hover:text-orange-400 transition-colors py-0.5"
          >
            View torch →
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

/** Collapsible "Sealed Torches" section */
function SealedTorchesSection({ torches }: { torches: ParsedChain[] }) {
  const [open, setOpen] = useState(false);

  if (torches.length === 0) return null;

  return (
    <div className="mt-8">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group mb-4"
      >
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <span className="font-medium">
          {torches.length} Sealed Torch{torches.length !== 1 ? 'es' : ''}
        </span>
        <span className="opacity-50">— chains sealed forever on Nostr</span>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {torches.map(tc => (
            <TorchDirectoryCard key={tc.genesis.slug} parsedChain={tc} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Index() {
  const { user } = useCurrentUser();
  const { data: allChains, isLoading } = useTorchDirectoryFull();

  // Split into lit vs extinguished once data resolves
  const litTorches = useMemo(() => (allChains ?? []).filter(c => c.state === 'lit'), [allChains]);
  const sealedTorches = useMemo(() => (allChains ?? []).filter(c => c.state === 'extinguished'), [allChains]);

  // Lit torches the signed-in user is currently holding — no extra query
  const myTorches = useMemo(() =>
    user ? litTorches.filter(c => c.currentHolder === user.pubkey) : [],
    [litTorches, user]
  );

  // Collect all pubkeys that have ever held a torch (genesis creators + pass recipients)
  const holderPubkeys = useMemo((): Set<string> => {
    const set = new Set<string>();
    for (const chain of allChains ?? []) {
      set.add(chain.genesis.pubkey);
      for (const cp of chain.canonicalChain) {
        set.add(cp.pass.pubkey);
        set.add(cp.pass.recipientPubkey);
      }
    }
    return set;
  }, [allChains]);

  const { data: communityNotes, isLoading: feedLoading } = useTorchliteFeed(holderPubkeys);

  return (
    <Layout>
      {/* Hero */}
      <div className="relative isolate overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 -z-20">
          <img src="/hero-bg.jpg" alt="" className="w-full h-full object-cover object-center" />
        </div>
        {/* Overlay: dark vignette so text stays readable */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/60 via-background/70 to-background" />
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/15 border border-orange-500/20 mb-6 shadow-[0_0_24px_rgba(249,115,22,0.3)]">
              <Flame className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Pass the Torch
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Gamifying Zaps on Nostr through digital torches that move between users.
              <br />
              Keep them alive. Don't let them go out.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/create">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2">
                  <Plus className="w-4 h-4" />
                  Light a Torch
                </Button>
              </Link>
              <Link to="/preview">
                <Button size="lg" variant="outline">
                  How it Works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Your Turn — only shown when signed in and holding at least one torch */}
      {user && myTorches.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="rounded-xl border border-border/60 px-5 py-5">
            {/* Header */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-4 h-4 text-orange-400 animate-pulse shrink-0" />
                <h2 className="font-semibold text-sm">
                  {myTorches.length === 1
                    ? 'You\'re holding a torch — pass it before it goes out!'
                    : `You're holding ${myTorches.length} torches — don't let them go out!`}
                </h2>
              </div>
              <Link
                to="/my-torches"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-orange-400 transition-colors ml-6"
              >
                View all my torches
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            {/* Cards — cap at 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myTorches.slice(0, 3).map(tc => (
                <MyTorchCard key={tc.genesis.slug} parsedChain={tc} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Community — scrolling card grid, between hero and how it works */}
      <CommunitySection notes={communityNotes} loading={feedLoading} />

      {/* How It Works */}
      <div className="bg-card/30 border-y border-border/40 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
                <Plus className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2">Light a Torch</h3>
              <p className="text-sm text-muted-foreground">
                Mint a new torch with a unique generative flame. Set its lifespan.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
                <Zap className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2">Pass it On</h3>
              <p className="text-sm text-muted-foreground">
                Pass it with a zap.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2">Keep it Lit</h3>
              <p className="text-sm text-muted-foreground">
                Each pass resets the clock. Let it die and it's gone forever.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Live Torches */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">Live Torches</h2>
            <p className="text-sm text-muted-foreground">
              Active torches burning on the Nostr network.
            </p>
          </div>
          {!isLoading && litTorches.length > 0 && (
            <Link to="/create">
              <Button variant="outline" size="sm" className="gap-1">
                <Plus className="w-4 h-4" />
                Light a Torch
              </Button>
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <TorchCardSkeleton key={i} />)}
          </div>
        ) : litTorches.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 motion-safe:animate-[fadeIn_0.5s_ease-out]">
            {litTorches.map(tc => (
              <TorchDirectoryCard key={tc.genesis.slug} parsedChain={tc} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-16 px-8 text-center">
              <Flame className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">
                No live torches right now.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Be the first to light one and start a chain.
              </p>
              <Link to="/create">
                <Button className="bg-orange-500 hover:bg-orange-600 gap-2">
                  <Plus className="w-4 h-4" />
                  Light the First Torch
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Sealed torches — collapsed by default */}
        {!isLoading && <SealedTorchesSection torches={sealedTorches} />}
      </div>

      {/* Bottom CTA */}
      <div className="relative isolate bg-gradient-to-b from-background to-orange-950/15 py-12">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.06)_0%,transparent_70%)]" />
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready?</h2>
          <p className="text-muted-foreground mb-6">
            Light a torch, pass it to a friend, and watch it travel the network.
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2">
              Light a Torch
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
